import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDisclosure } from "@chakra-ui/hooks";
import { useNavigate, useParams } from "react-router-dom";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import {
  MdAdd,
  MdEdit,
  MdFileDownload,
  MdFileUpload,
  MdGroups,
  MdHowToReg,
  MdPeople,
  MdPersonOff,
  MdVolunteerActivism,
} from "react-icons/md";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import notify from "lib/notify";
import { roleTone, statusTone } from "lib/statusTone";
import AtriumApi, { getApiErrorMessage } from "api";
import Avatar from "components/avatar";
import Badge from "components/badge/Badge";
import Button, { IconButton } from "components/button";
import DataTable from "components/data-table";
import SearchInput from "components/fields/SearchInput";
import Select from "components/fields/Select";
import { PageContainer, PageHeader } from "components/page";
import StatCard from "components/stat-card";
import { EmptyState, ErrorState } from "components/states";
import TagBadge from "components/tags/TagBadge";
import TagFilter from "components/tags/TagFilter";
import { EMPTY, formatNumber } from "utils/format";
import UserDetailsDrawer from "./user-details";
import NewUser from "./new-user";
import UploadCsvDialog from "./upload-csv-dialog";

const STATS = [
  { key: "totalUsers", label: "Total Users", icon: MdPeople, accent: "slate" },
  { key: "active", label: "Active", icon: MdHowToReg, accent: "sage" },
  { key: "inactive", label: "Inactive", icon: MdPersonOff, accent: "danger" },
  { key: "members", label: "Members", icon: MdGroups, accent: "sky" },
  { key: "volunteers", label: "Volunteers", icon: MdVolunteerActivism, accent: "gold" },
];

const ROLE_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "guest", label: "Guest" },
  { value: "member", label: "Member" },
  { value: "volunteer", label: "Volunteer" },
  { value: "deacon", label: "Deacon" },
  { value: "elder", label: "Elder" },
  { value: "staff", label: "Staff" },
  { value: "admin", label: "Admin" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const PAGE_SIZE_OPTIONS = [10, 25];
const EMPTY_PAGINATION = { rowCount: 0, page: 1, per: 10 };

const fullName = (user) => [user?.firstName, user?.lastName].filter(Boolean).join(" ");

const loadErrorMessage = (error) => {
  const status = error?.response?.status;
  const serverMessage = error?.response?.data?.message;
  if (status === 403) return "Authentication required. Please log in again.";
  if (status === 401) return "Your session expired. Please log in again.";
  if (serverMessage === "Missing church slug header") {
    return "Church organization not found. Please check your subdomain.";
  }
  if (serverMessage === "Church not found") {
    return `Organization not found for subdomain: ${window.location.hostname.split(".")[0]}`;
  }
  return getApiErrorMessage(error, "People could not be loaded.");
};

const People = () => {
  useEffect(() => {
    track(EVENTS.PEOPLE_PAGE_VIEWED);
  }, []);

  const navigate = useNavigate();
  const params = useParams();
  const { isOpen, onOpen, onClose } = useDisclosure({
    defaultIsOpen: !!params.id,
    onClose: () => {
      setUser(undefined); // Clear user state to prevent stale data
      navigate("/admin/people");
    },
  });

  const [openCsvDialog, setOpenCsvDialog] = useState(false);
  const [{ rowCount, page, per }, setPagination] = useState(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queryOptions, setQueryOptions] = useState({ page: 1, per: 10 });
  const [user, setUser] = useState(undefined);
  const [statsSummary, setStatsSummary] = useState(undefined);
  const [exportLoading, setExportLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const updateQuery = (changes) =>
    setQueryOptions((prev) => ({ ...prev, ...changes, page: 1 }));

  const getUsers = useCallback(() => {
    setLoading(true);
    setError(null);

    return AtriumApi.get("/v1/users", { params: queryOptions })
      .then((response) => {
        setUsers(response.data.users || []);
        setPagination(response.data.pagination || EMPTY_PAGINATION);
        setStatsSummary(response.data.summary || {});
      })
      .catch((err) => {
        console.error("Error fetching users:", err);
        setError(err);
        setUsers([]);
        setPagination(EMPTY_PAGINATION);
        setStatsSummary({});
      })
      .finally(() => {
        setLoading(false);
      });
  }, [queryOptions]);

  useEffect(() => {
    getUsers();
  }, [params.id, getUsers]);

  const addUser = () => {
    setUser(undefined);
    onOpen();
    navigate("/admin/people/new");
  };

  const editProfile = useCallback(
    (person) => navigate("/admin/people/" + person.id + "/edit"),
    [navigate]
  );

  const showProfile = (person) => {
    setUser(person);
    onOpen();
    navigate("/admin/people/" + person.id);
  };

  const handleExportToCSV = async () => {
    try {
      setExportLoading(true);
      const response = await AtriumApi.get("/v1/users/get-all-users"); // all users, not paginated
      const allUsers = response?.data?.data || [];

      if (!allUsers.length) {
        notify.error("No people to export");
        return;
      }

      const cleanedUsers = allUsers.map((u) => ({
        campus_name: u?.campus_name || "",
        household_id: u?.household_id || "",
        household_avatar: u?.household_avatar || "",
        household_member_count: u?.household_member_count || "",
        household_name: u?.household_name,
        household_primary_contact_id: u?.household_primary_contact_id || "",
        household_primary_contact_name: u?.household_primary_contact_name || "",
        user_email: u?.user_email || "",
        user_phone: u?.user_phone || "",
        user_gender: u?.user_gender || "",
        user_date_of_birth: u?.user_date_of_birth || "",
        user_first_name: u?.user_first_name || "",
        user_last_name: u?.user_last_name || "",
        user_family_relationship: u?.user_family_relationship || "",
        user_city: u?.user_city || "",
        user_state: u?.user_state || "",
        user_country: u?.user_country || "",
        user_zip_code: u?.user_zip_code || "",
        user_avatar: u?.user_avatar || "",
        user_street: u?.user_street || "",
        user_people_id: u?.user_people_id || "",
        user_is_primary: u?.user_is_primary || "",
        user_relationship_text: u?.user_relationship_text || "",
        user_allergies: u?.user_allergies || "",
        user_school_grade: u?.user_school_grade || "",
      }));
      const csv = Papa.unparse(cleanedUsers);

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "all-users.csv");
      notify.success("People exported");
    } catch (err) {
      console.error("Export error", err);
      notify.error(getApiErrorMessage(err, "Could not export people"));
    } finally {
      setExportLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        accessorFn: fullName,
        cell: ({ row }) => (
          <div className="inline-flex items-center gap-3">
            <Avatar user={row.original} size="sm" noBorder />
            <span className="whitespace-nowrap font-medium text-fg">
              {fullName(row.original)}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: (info) => <span className="text-fg-muted">{info.getValue() || EMPTY}</span>,
      },
      {
        accessorKey: "role",
        header: "Role",
        size: 130,
        cell: (info) =>
          info.getValue() ? (
            <Badge tone={roleTone(info.getValue())} className="capitalize">
              {info.getValue()}
            </Badge>
          ) : (
            EMPTY
          ),
      },
      {
        accessorKey: "id",
        header: "ID",
        size: 90,
        cell: (info) => <span className="text-fg-muted">{info.getValue()}</span>,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        cell: (info) =>
          info.getValue() ? (
            <Badge tone={statusTone(info.getValue())} dot className="capitalize">
              {info.getValue()}
            </Badge>
          ) : (
            EMPTY
          ),
      },
      {
        accessorKey: "tags",
        header: "Tags",
        size: 220,
        cell: (info) => {
          const tags = info.getValue() || [];
          if (tags.length === 0) return <span className="text-fg-muted">{EMPTY}</span>;
          return (
            <div className="flex flex-wrap items-center gap-1">
              {tags.slice(0, 3).map((tag) => (
                <TagBadge key={tag.id} tag={tag} size="sm" />
              ))}
              {tags.length > 3 && (
                <span className="text-xs font-medium text-fg-muted">+{tags.length - 3}</span>
              )}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        size: 64,
        meta: { align: "right" },
        cell: ({ row }) => (
          <IconButton
            icon={<MdEdit className="h-5 w-5" />}
            label={`Edit ${fullName(row.original)}`}
            size="sm"
            onClick={() => editProfile(row.original)}
          />
        ),
      },
    ],
    [editProfile]
  );

  const hasFilters = Boolean(
    queryOptions.q || queryOptions.role || queryOptions.status || queryOptions.tags?.length
  );

  const clearFilters = () =>
    setQueryOptions((prev) => ({ page: 1, per: prev.per }));

  const isNew = params.id === "new";

  return (
    <PageContainer>
      <PageHeader
        title="People"
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={<MdFileUpload className="h-4 w-4" />}
              onClick={() => setOpenCsvDialog(true)}
            >
              Import CSV
            </Button>
            <Button
              variant="secondary"
              leftIcon={<MdFileDownload className="h-4 w-4" />}
              loading={exportLoading}
              onClick={handleExportToCSV}
            >
              {exportLoading ? "Exporting…" : "Export CSV"}
            </Button>
            <Button leftIcon={<MdAdd className="h-4 w-4" />} onClick={addUser}>
              Add Person
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-5 md:grid-cols-3 xl:grid-cols-5">
        {STATS.map((stat) => (
          <StatCard
            key={stat.key}
            icon={stat.icon}
            accent={stat.accent}
            label={stat.label}
            value={formatNumber(statsSummary?.[stat.key] ?? 0)}
            loading={statsSummary === undefined}
          />
        ))}
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
        <SearchInput
          value={queryOptions.q || ""}
          onChange={(q) => updateQuery({ q })}
          placeholder="Search by name, email, or phone…"
          ariaLabel="Search people"
          className="w-full lg:w-80"
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:flex">
          <Select
            aria-label="Filter by role"
            value={queryOptions.role || ""}
            onChange={(e) => updateQuery({ role: e.target.value })}
            options={ROLE_OPTIONS}
            className="w-full lg:w-40"
          />
          <Select
            aria-label="Filter by status"
            value={queryOptions.status || ""}
            onChange={(e) => updateQuery({ status: e.target.value })}
            options={STATUS_OPTIONS}
            className="w-full lg:w-40"
          />
          <TagFilter
            selectedTagIds={queryOptions.tags || []}
            onChange={(tagIds) => updateQuery({ tags: tagIds })}
            className="w-full lg:w-48"
          />
        </div>
      </div>

      {error ? (
        <ErrorState message={loadErrorMessage(error)} onRetry={getUsers} />
      ) : (
        <DataTable
          aria-label="People"
          columns={columns}
          data={users}
          loading={loading}
          getRowId={(row) => String(row.id)}
          onRowClick={showProfile}
          emptyState={
            hasFilters ? (
              <EmptyState
                variant="filtered"
                title="No people match your filters"
                action={
                  <Button variant="secondary" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={MdPeople}
                title="No People Yet"
                description="Add people one at a time or import them from a CSV file."
                action={
                  <Button leftIcon={<MdAdd className="h-4 w-4" />} onClick={addUser}>
                    Add Person
                  </Button>
                }
              />
            )
          }
          pagination={{
            pageIndex: page - 1,
            pageSize: per,
            total: rowCount,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            onPageChange: (index) =>
              setQueryOptions((prev) => ({ ...prev, page: index + 1, per })),
            onPageSizeChange: (size) =>
              setQueryOptions((prev) => ({ ...prev, page: 1, per: size })),
          }}
        />
      )}

      <NewUser isOpen={isOpen && isNew} onClose={onClose} onSuccess={getUsers} />
      <UserDetailsDrawer
        isOpen={isOpen && Boolean(params.id) && !isNew}
        onClose={onClose}
        user={user}
      />

      {openCsvDialog && (
        <UploadCsvDialog
          openCsvDialog={openCsvDialog}
          setOpenCsvDialog={setOpenCsvDialog}
          getUsers={getUsers}
        />
      )}
    </PageContainer>
  );
};

export default People;
